'use client'

import {
    Box,
    ButtonBase,
    Collapse,
    IconButton,
    Popover,
    Tooltip,
    Typography,
} from '@mui/material'
import { C1CircularProgress } from 'c1ui/components/C1CircularProgress/C1CircularProgress'
import { C1Icon } from 'components/core/icons/C1Icon'
import { Braces, ChevronDown, ChevronRight, FunctionSquare } from 'lucide-react'
import { FC, memo, MouseEvent, useCallback, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { CELSchema } from './CELContext'

interface VariableBrowserProps {
    schema: CELSchema
    onInsert?: (text: string) => void
}

interface FieldType {
    name: string
    fields?: Array<{ name: string; typeName: string; description?: string }>
}

interface VariableItemProps {
    name: string
    typeName: string
    path: string
    types: FieldType[]
    onInsert?: (text: string) => void
    depth?: number
}

const VariableItem: FC<VariableItemProps> = ({ name, typeName, path, types, onInsert, depth = 0 }) => {
    const [expanded, setExpanded] = useState(false)

    const fields = useMemo(() => types.find((t) => t.name === typeName)?.fields ?? [], [types, typeName])

    const handleToggle = useCallback((e: React.MouseEvent): void => {
        e.stopPropagation()
        setExpanded((prev) => !prev)
    }, [])

    const handleClick = useCallback((): void => {
        onInsert?.(path)
    }, [path, onInsert])

    // If variable has fields, show as expandable with collapse
    if (fields.length > 0) {
        return <Box>
            <ButtonBase
                onClick={handleToggle}
                aria-expanded={expanded}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    py: 1,
                    px: 2,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'grey.50' },
                }}
            >
                {expanded
                    ? <C1Icon icon={ChevronDown} sx={{ fontSize: 18, mr: 1 }} />
                    : <C1Icon icon={ChevronRight} sx={{ fontSize: 18, mr: 1 }} />}
                <Typography sx={{ fontWeight: 500, mr: 1 }}>
                    {name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {typeName}
                </Typography>
            </ButtonBase>
            <Collapse in={expanded} timeout="auto">
                <Box sx={{ ml: 3 }}>
                    {fields.map((field) => <VariableItem
                        key={field.name}
                        name={field.name}
                        typeName={field.typeName}
                        path={`${path}.${field.name}`}
                        types={types}
                        onInsert={onInsert}
                        depth={depth + 1}
                    />)}
                </Box>
            </Collapse>
        </Box>
    }

    // Otherwise show as simple clickable item
    return <ButtonBase
        onClick={handleClick}
        sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            justifyContent: 'flex-start',
            textAlign: 'left',
            py: 1,
            px: 2,
            borderRadius: 1,
            cursor: 'pointer',
            '&:hover': { backgroundColor: 'primary.extraLight' },
        }}
    >
        <Typography sx={{ mr: 1 }}>
            {name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
            {typeName}
        </Typography>
    </ButtonBase>
}

interface FunctionItemProps {
    name: string
    paramTypes: string[]
    returnType: string
    onInsert?: (text: string) => void
}

const FunctionItem: FC<FunctionItemProps> = ({
    name,
    paramTypes,
    returnType,
    onInsert,
}) => {
    const handleClick = useCallback((): void => {
        // Insert the function with placeholder params
        const placeholders = paramTypes.map((t, i) => `arg${i}`).join(', ')
        onInsert?.(`${name}(${placeholders})`)
    }, [name, paramTypes, onInsert])

    const signature = useMemo((): string => {
        const params = paramTypes.join(', ')
        return `(${params}) → ${returnType}`
    }, [paramTypes, returnType])

    return <ButtonBase
        onClick={handleClick}
        sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            width: '100%',
            textAlign: 'left',
            py: 1,
            pl: 5,
            pr: 1,
            borderRadius: 1,
            cursor: 'pointer',
            '&:hover': { backgroundColor: 'primary.extraLight' },
        }}
    >
        <Typography>
            {name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
            {signature}
        </Typography>
    </ButtonBase>
}

interface ConstantItemProps {
    name: string
    onInsert?: (text: string) => void
}

const ConstantItem: FC<ConstantItemProps> = ({ name, onInsert }) => {
    const handleClick = useCallback((): void => {
        onInsert?.(name)
    }, [name, onInsert])

    return <ButtonBase
        onClick={handleClick}
        sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            justifyContent: 'flex-start',
            textAlign: 'left',
            py: 1,
            pl: 5,
            pr: 1,
            borderRadius: 1,
            cursor: 'pointer',
            '&:hover': { backgroundColor: 'primary.extraLight' },
        }}
    >
        <Typography sx={{ fontWeight: 500, mr: 1 }}>
            {name}
        </Typography>
    </ButtonBase>
}

interface CollapsibleSectionProps {
    title: React.ReactNode
    icon?: React.ReactNode
    children: React.ReactNode
    defaultExpanded?: boolean
}

const CollapsibleSection: FC<CollapsibleSectionProps> = ({
    title,
    icon,
    children,
    defaultExpanded = false,
}) => {
    const [expanded, setExpanded] = useState(defaultExpanded)

    const handleToggle = useCallback((): void => {
        setExpanded((prev) => !prev)
    }, [])

    return <Box>
        <ButtonBase
            onClick={handleToggle}
            aria-expanded={expanded}
            sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                justifyContent: 'flex-start',
                textAlign: 'left',
                py: 1,
                px: 2,
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'grey.50' },
            }}
        >
            {expanded
                ? <C1Icon icon={ChevronDown} sx={{ fontSize: 18, mr: 1 }} />
                : <C1Icon icon={ChevronRight} sx={{ fontSize: 18, mr: 1 }} />}
            {icon && <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', color: 'primary.main' }}>{icon}</Box>}
            <Typography sx={{ fontWeight: 600 }}>
                {title}
            </Typography>
        </ButtonBase>
        <Collapse in={expanded} timeout="auto">
            <Box sx={{ pl: 1 }}>
                {children}
            </Box>
        </Collapse>
    </Box>
}

/**
 * VariableBrowserContent displays the schema items without the popover wrapper.
 * Used internally by VariableBrowserPopover.
 * Memoized to prevent re-renders when parent component updates.
 */
export const VariableBrowserContent: FC<VariableBrowserProps> = memo(({ schema, onInsert }) => {
    const { variables, functions, constants, types, isLoading } = schema
    const intl = useIntl()

    // Group functions by namespace
    const groupedFunctions = useMemo<Record<string, typeof functions>>(() => {
        const groups: Record<string, typeof functions> = {}
        const standardLabel = intl.formatMessage({ id: 'cel_namespace_standard', defaultMessage: 'Standard' })
        functions.forEach((fn) => {
            const parts = fn.name.split('.')
            const namespace = parts.length > 2 ? parts.slice(0, -1).join('.') : standardLabel
            if (!groups[namespace]) {
                groups[namespace] = []
            }
            groups[namespace].push(fn)
        })
        return groups
    }, [functions, intl])

    // Filter constants to show only dot-notation names (e.g., AppUserType.SERVICE_ACCOUNT)
    // and exclude legacy SCREAMING_SNAKE_CASE names (e.g., APP_USER_TYPE_SERVICE_ACCOUNT)
    const filteredConstants = useMemo<typeof constants>(() => constants.filter((c) =>
        c.name.includes('.')), [constants])

    if (isLoading) {
        return <Box sx={{ p: 2, textAlign: 'center' }}>
            <C1CircularProgress size={24} />
        </Box>
    }

    return <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
        {/* Header */}
        <Box sx={{ px: 2, py: 2, borderBottom: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="body1" component="div" sx={{ fontWeight: 600 }}>
                <FormattedMessage id="cel_browser_title" defaultMessage="Available variables" />
            </Typography>
            <Typography variant="caption" color="text.secondary">
                <FormattedMessage id="cel_browser_hint" defaultMessage="Click to insert into expression" />
            </Typography>
        </Box>

        {/* Variables Section */}
        {variables.map((v) => <VariableItem
            key={v.name}
            name={v.name}
            typeName={v.typeName}
            path={v.name}
            types={types}
            onInsert={onInsert}
        />)}

        {/* Functions Section */}
        {functions.length > 0 && <CollapsibleSection
            title={<FormattedMessage id="cel_section_functions" defaultMessage="Functions" />}
            icon={<C1Icon icon={FunctionSquare} sx={{ fontSize: 16 }} />}
        >
            {Object.entries(groupedFunctions).map(([namespace, fns]) => <Box key={namespace}>
                <Typography
                    variant="h6"
                    component="div"
                    sx={{
                        display: 'block',
                        fontWeight: 600,
                        color: 'text.secondary',
                        px: 2,
                        pt: 2,
                        pb: 1,
                    }}
                >
                    {namespace}
                </Typography>
                {fns.map((fn) => <FunctionItem
                    key={fn.name}
                    name={fn.name}
                    paramTypes={fn.paramTypes}
                    returnType={fn.returnType}
                    onInsert={onInsert}
                />)}
            </Box>)}
        </CollapsibleSection>}

        {/* Constants Section */}
        {filteredConstants.length > 0 && <CollapsibleSection
            title={<FormattedMessage id="cel_section_constants" defaultMessage="Constants" />}
        >
            {filteredConstants.map((c) => <ConstantItem
                key={c.name}
                name={c.name}
                onInsert={onInsert}
            />)}
        </CollapsibleSection>}
    </Box>
})

interface VariableBrowserButtonProps {
    schema: CELSchema
    onInsert?: (text: string) => void
}

/**
 * VariableBrowserButton is a button that opens a popover with the variable browser.
 * Use this component in the editor toolbar.
 */
export const VariableBrowserButton: FC<VariableBrowserButtonProps> = ({ schema, onInsert }) => {
    const intl = useIntl()
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

    const handleClick = useCallback((event: MouseEvent<HTMLButtonElement>): void => {
        setAnchorEl(event.currentTarget)
    }, [])

    const handleClose = useCallback((): void => {
        setAnchorEl(null)
    }, [])

    const handleInsert = useCallback((text: string): void => {
        onInsert?.(text)
        handleClose()
    }, [onInsert, handleClose])

    const open = Boolean(anchorEl)

    return <>
        <Tooltip
            title={
                <FormattedMessage
                    id="cel_browser_button_tooltip"
                    defaultMessage="Browse available variables"
                />
            }
        >
            <IconButton
                onClick={handleClick}
                size="small"
                sx={{
                    color: 'grey.600',
                    width: 28,
                    height: 28,
                    '&:hover': { backgroundColor: 'grey.100' },
                }}
                aria-label={intl.formatMessage({ id: 'editor.browseVariables', defaultMessage: 'Browse variables' })}
            >
                <C1Icon icon={Braces} sx={{ fontSize: 18 }} />
            </IconButton>
        </Tooltip>
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            slotProps={{
                paper: {
                    sx: { width: 400, maxHeight: 500 },
                },
            }}
        >
            <VariableBrowserContent schema={schema} onInsert={handleInsert} />
        </Popover>
    </>
}
